<template>
    <transition>
        <div class="container-fluid page-content">
            <!-- Modern Page Header -->
            <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center page-header-row gap-3">
                <div>
                    <h4 class="page-title text-slate-800 fw-bold mb-1">Department</h4>
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb mb-0">
                            <li class="breadcrumb-item"><a href="/" class="text-muted"><i class="fas fa-home me-1"></i>Home</a></li>
                            <li class="breadcrumb-item"><a href="javascript:void(0);" class="text-muted">Settings</a></li>
                            <li class="breadcrumb-item active text-indigo fw-medium" aria-current="page">Department List</li>
                        </ol>
                    </nav>
                </div>
                <div>
                    <button type="button" class="btn btn-premium d-flex align-items-center gap-2" @click="toggleModal()" v-if="permission['department-create']">
                        <i class="fas fa-plus"></i> Add New Department
                    </button>
                </div>
            </div>

            <!-- Modern Filter Dashboard Bar -->
            <div class="card border-0 shadow-sm filter-card mb-4" v-if="items.length > 0 || search || filterCompanyId">
                <div class="card-body">
                    <div class="row align-items-center justify-content-between g-3">
                        <div class="col-12 col-md-auto d-flex align-items-center gap-2">
                            <span class="text-muted text-nowrap">Show</span>
                            <select class="form-select form-select-custom length-select" v-model="perPage">
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="all">All</option>
                            </select>
                            <span class="text-muted text-nowrap">Entries</span>
                        </div>

                        <div class="col-12 col-md-auto d-flex flex-wrap align-items-center gap-3 ms-md-auto">
                            <!-- Company Filter -->
                            <div class="filter-select-wrapper" style="min-width: 200px;">
                                <select class="form-select form-select-custom" v-model="filterCompanyId">
                                    <option value="">All Companies</option>
                                    <option v-for="company in companies" :key="company.id" :value="company.id">
                                        {{ company.name }}
                                    </option>
                                </select>
                            </div>

                            <div class="position-relative search-container">
                                <i class="fas fa-search search-icon text-muted"></i>
                                <input type="text" class="form-control-custom search-input" v-model="search" placeholder="Search departments...">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modern Table List Card -->
            <div class="card border-0 shadow-sm table-card">
                <div class="card-body p-0">
                    <div class="table-responsive">
                        <table class="table table-premium mb-0" v-if="!loading && paginatedItems.length > 0">
                            <thead>
                                <tr>
                                    <th style="width: 80px;">SL</th>
                                    <th>Department Name</th>
                                    <th>Company Name</th>
                                    <th style="width: 150px;">Status</th>
                                    <th class="text-end" style="width: 100px;">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, i) in paginatedItems" :key="i">
                                    <td>{{ (currentPage - 1) * (perPage === 'all' ? 0 : parseInt(perPage)) + i + 1 }}</td>
                                    <td class="fw-bold text-slate-800">{{ item.name }}</td>
                                    <td>{{ item.company_name }}</td>
                                    <td>
                                        <span v-if="item.status==1" class="badge-role badge-role-active">Active</span>
                                        <span v-if="item.status==0" class="badge-role badge-role-inactive">Inactive</span>
                                    </td>
                                    <td class="text-end">
                                        <div class="dropdown d-inline-block">
                                            <button class="btn btn-action-dropdown dropdown-toggle arrow-none" data-bs-toggle="dropdown" aria-expanded="false">
                                                <i class="fas fa-ellipsis-v"></i>
                                            </button>
                                            <div class="dropdown-menu dropdown-menu-end shadow-sm border-0 dropdown-premium">
                                                <a href="javascript:void(0);" class="dropdown-item d-flex align-items-center gap-2 text-warning" @click="edit(item)" v-if="permission['department-edit']">
                                                    <i class="fas fa-edit me-2"></i> Edit
                                                </a>
                                                <a href="javascript:void(0);" class="dropdown-item d-flex align-items-center gap-2 text-danger" @click="deleteItem(item)" v-if="permission['department-delete']">
                                                    <i class="fas fa-trash-alt me-2"></i> Remove
                                                </a>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Empty State -->
                        <div v-if="!loading && filteredItems.length === 0" class="text-center py-5">
                            <div class="mb-3">
                                <i class="fas fa-inbox fa-3x text-muted opacity-50"></i>
                            </div>
                            <h6 class="text-muted fw-normal">No departments found</h6>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loading" class="d-flex justify-content-center align-items-center py-5">
                            <div class="spinner-premium"></div>
                        </div>
                    </div>
                </div>

                <!-- Table Footer with Pagination -->
                <div class="card-footer bg-white border-0 py-3 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3" v-if="!loading && filteredItems.length > 0">
                    <div class="text-muted small">
                        Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} entries
                    </div>
                    <Pagination :pagination="pagination" :language="lang" @onChangePage="setPage"></Pagination>
                </div>
            </div>

            <!-- Modal -->
            <Modal @close="toggleModal()" :modalActive="modalActive">
                <div class="modal-content border-0 shadow-lg modal-premium modal-premium-sm">
                    <div class="modal-header border-0 pb-0">
                        <h4 class="fw-bold m-0 text-slate-800">{{ editMode ? 'Update Department' : 'Create New Department' }}</h4>
                        <button @click="toggleModal()" type="button" class="btn-close-custom">✕</button>
                    </div>
                    <form @submit.prevent="submitForm()" enctype="multipart/form-data">
                        <div class="modal-body py-4">
                            <div class="mb-3">
                                <label for="name" class="form-label-custom text-slate-700 fw-semibold mb-2">Department Name *</label>
                                <input type="text" class="form-control form-control-custom" :class="{'is-invalid': errors.name}" @keypress="onkeyPress('name')" v-model="form.name" id="name" placeholder="Department name" autocomplete="off">
                                <div class="invalid-feedback" v-if="errors.name">
                                    {{errors.name[0]}}
                                </div>
                            </div>

                            <div class="mb-3">
                                <label for="company_id" class="form-label-custom text-slate-700 fw-semibold mb-2">Company</label>
                                <select class="form-select form-select-custom" :class="{'is-invalid': errors.company_id}" v-model="form.company_id" @change="onkeyPress('company_id')" id="company_id">
                                    <option value="0">Select Company</option>
                                    <option v-for="(company, index) in companies" :value="company.id" :key="index">{{ company.name }}</option>
                                </select>
                                <div class="invalid-feedback" v-if="errors.company_id">
                                    {{errors.company_id[0]}}
                                </div>
                            </div>

                            <div class="mb-0">
                                <label for="dstatus" class="form-label-custom text-slate-700 fw-semibold mb-2">Status</label>
                                <select class="form-select form-select-custom" :class="{'is-invalid': errors.status}" v-model="form.status" @change="onkeyPress('status')" id="dstatus">
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </select>
                                <div class="invalid-feedback" v-if="errors.status">
                                    {{errors.status[0]}}
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer border-0 pt-0 justify-content-end gap-2">
                            <button type="button" class="btn btn-outline-premium px-4" @click="toggleModal()">Cancel</button>
                            <button type="submit" class="btn btn-premium px-4" :disabled="disabled">
                                <span v-show="isSubmit" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {{ editMode ? 'Update' : 'Create' }}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    </transition>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { useProductStore } from '@/stores/product';
import { useRequisitionProductStore } from '@/stores/requisitionProduct';


import Modal from "./../helper/Modal.vue";
import Pagination from '@/components/Pagination.vue';
import { ref, onMounted } from "vue";
import Form from "vform";
import axios from "axios";

export default {
    name: "Department",
    components: {
        Modal,
        Pagination
    },
    data() {
        return {
            loading: true,
            isSubmit: false,
            showModal: false,
            editMode:false,
            disabled: false,
            modalActive:false,
            errors: {},
            btn:'Create',
            items: [],
            companies: [],
            search: '',
            filterCompanyId: '',
            perPage: 10,
            currentPage: 1,
            lang: {
                info: 'Showing_FROM_to_TO_of_TOTAL_entries',
            },
            form: new Form({
                id: '',
                name: '',
                company_id: 0,
                status: 1,
            }),
        }
    },
    created() {
        this.fetchDepartmentData();
        this.fetchCompanyData();
    },
    watch: {
        search() {
            this.currentPage = 1;
        },
        filterCompanyId() {
            this.currentPage = 1;
        },
        perPage() {
            this.currentPage = 1;
        }
    },
    methods: {
        toggleModal: function() {
            this.modalActive = !this.modalActive;  
            if(!this.modalActive){
                this.editMode = false;
                this.btn='Create';
            } 
            this.errors = '';
            this.isSubmit = false;
            this.form.reset(); 
            console.log('then',this.isSubmit)
        },
        fetchDepartmentData() { 
            axios.get(this.apiUrl+'/departments', this.headerjson) 
            .then((res) => {
                this.items = res.data.data;
            })
            .catch((err) => {
                this.$toast.error(err.response.data.message);
            })
            .finally((fres) => {
                this.loading = false;
            }); 
        },

        fetchCompanyData() { 
            axios.get(this.apiUrl+'/companies', this.headerjson)  
            .then((res) => { 
                this.companies = res.data.data;
            })
            .catch((err) => {
                this.$toast.error(err.response.data.message);
            });
        },

        edit: function(item) {
            this.btn='Update';
            this.editMode = true;
            this.toggleModal();
            this.form.fill(item); 
        },
        submitForm: function(e) { 
            
            this.isSubmit = true;
            this.disabled = true;
            const formData = new FormData();  
            formData.append('name', this.form.name);
            formData.append('company_id', this.form.company_id);
            formData.append('status', this.form.status); 
            this.form.logo ? formData.append('logo', this.form.logo, this.form.logo.name) : '';
            if(this.editMode){
                formData.append('_method', 'put');
                var postEvent = axios.post(this.apiUrl+'/departments/'+this.form.id, formData, this.headers);
            }else{  
                var postEvent = axios.post(this.apiUrl+'/departments', formData, this.headers);
            }          

            postEvent.then(res => {                
                this.isSubmit = false;
                this.disabled = false;
                if(res.status == 200){
                    this.toggleModal();
                    this.fetchDepartmentData();
                    this.$toast.success(res.data.message); 
                }else{
                    this.$toast.error(res.data.message);
                } 
            }).catch(err => {  
                this.isSubmit = false;
                this.disabled = false;
                this.$toast.error(err.response.data.message);
                if(err.response.status == 422){
                    this.errors = err.response.data.errors 
                }
            })
        },

        onkeyPress: function(field) { 
            for (var k in this.errors){     // Loop through the object
                if(k==field){      // If the current key contains the string we're looking for 
                    delete this.errors[k];  // Delete obj[key];
                }
            }  
        },
        deleteItem: function(item) { 
            this.$swal({
                title: 'Are you sure?',
                text: "You want delete this item!", 
                showCancelButton: true,
                confirmButtonCategory: '#3085d6',
                cancelButtonCategory: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => { 
                if (result.value) { 
                    axios.delete(this.apiUrl+'/departments/'+item.id, this.headerjson)
                     .then(res => {
                        if(res.status == 200){ 
                            this.fetchDepartmentData();
                            this.$toast.success(res.data.message); 
                        }else{
                            this.$toast.error(res.data.message);
                        } 
                     }).catch(err => {  
                        this.$toast.error(err.response.data.message);
                        if(err.response.status == 422){
                            this.errors = err.response.data.errors 
                        }
                     }) 
                }
            }); 
        },
        setPage(data) {
            if (data.url === 'prev') {
                if (this.currentPage > 1) this.currentPage--;
            } else if (data.url === 'next') {
                if (this.currentPage < this.pagination.lastPage) this.currentPage++;
            } else if (data.url) {
                this.currentPage = parseInt(data.url);
            }
        }
    },
    destroyed() {},
    mounted() {
        window.scrollTo(0, 0);
    },
    computed: {
        permission() {
            let pname = this.$route.meta.parent_module;
            let module_name = this.$route.meta.module_name;
            let path_name = this.$route.path; 
            let data = '';
            if(this.$route.meta.parent_module){
                data = this.$store.getters.userAllPermissions[pname][0].children[path_name]
            }else{
                data = this.$store.getters.userAllPermissions[module_name][0].other_actions; 
            } 
            return data;
        },
        filteredItems() {
            let list = this.items;
            if (this.filterCompanyId) {
                list = list.filter(item => item.company_id == this.filterCompanyId);
            }
            if (this.search) {
                const query = this.search.toLowerCase();
                list = list.filter(item => {
                    return (
                        (item.name && item.name.toLowerCase().includes(query)) ||
                        (item.company_name && item.company_name.toLowerCase().includes(query))
                    );
                });
            }
            return list;
        },
        paginatedItems() {
            if (this.perPage === 'all') {
                return this.filteredItems;
            }
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + parseInt(this.perPage);
            return this.filteredItems.slice(start, end);
        },
        pagination() {
            const total = this.filteredItems.length;
            const perPage = this.perPage === 'all' ? total : parseInt(this.perPage);
            const lastPage = perPage > 0 ? Math.ceil(total / perPage) : 1;
            const currentPage = this.currentPage;
            const from = total > 0 ? (currentPage - 1) * perPage + 1 : 0;
            const to = Math.min(currentPage * perPage, total);
            
            const links = [];
            
            links.push({
                url: currentPage > 1 ? 'prev' : null,
                label: '&laquo; Previous',
                active: false
            });
            
            for (let i = 1; i <= lastPage; i++) {
                links.push({
                    url: i.toString(),
                    label: i.toString(),
                    active: i === currentPage
                });
            }
            
            links.push({
                url: currentPage < lastPage ? 'next' : null,
                label: 'Next &raquo;',
                active: false
            });

            return {
                lastPage,
                currentPage,
                total,
                from,
                to,
                links
            };
        }
    }
}
</script>